import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import type { ChatAttachment } from '../lib/chat-attachments'
import type { ChatSource } from '../components/ChatSources'
import { withoutFileData } from '../lib/chat-attachments'

export interface InventoryCitation {
  principio_activo: string
  nombre_comercial: string | null
  laboratorio: string | null
  presentacion: string | null
  concentracion: string | null
  stock_total: number
  cantidad_caja_1: number
  cantidad_caja_2: number
  cantidad_caja_3: number
  proxima_caducidad: string | null
  dias_a_caducar: number | null
  condicion: string | null
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  citations?: InventoryCitation[]
  moderated?: boolean
  attachments?: ChatAttachment[]
  /** On an answer: the medicines read from the files of the question it answers */
  seen_in_files?: string
  /** On an answer: the MedlinePlus pages consulted for it */
  sources?: ChatSource[]
}

interface ConversationSession {
  id: string
  messages: ChatMessage[]
  createdAt: string
  preview: string
}

interface DoctorRobotinContextType {
  messages: ChatMessage[]
  sessionId: string
  sessions: ConversationSession[]
  loading: boolean
  historyOpen: boolean
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  addMessage: (message: ChatMessage) => void
  clearConversation: () => void
  startNewConversation: () => void
  loadSession: (sessionId: string) => void
  setHistoryOpen: (open: boolean) => void
  fetchSessions: () => Promise<void>
}

const DoctorRobotinContext = createContext<DoctorRobotinContextType | undefined>(undefined)

// Get or create session ID from sessionStorage
const getOrCreateSessionId = (): string => {
  let sessionId = sessionStorage.getItem('dr-robotin-session')
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('dr-robotin-session', sessionId)
  }
  return sessionId
}

// Load messages from sessionStorage
const loadMessagesFromStorage = (): ChatMessage[] => {
  try {
    const stored = sessionStorage.getItem('dr-robotin-messages')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Error loading messages from storage:', e)
  }
  return []
}

export function DoctorRobotinProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>(loadMessagesFromStorage)
  const [sessionId, setSessionId] = useState<string>(getOrCreateSessionId)
  const [sessions, setSessions] = useState<ConversationSession[]>([])
  const [loading, setLoading] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  // Persist messages to sessionStorage whenever they change
  useEffect(() => {
    try {
      // The files stay in memory only; their thumbnails are small enough to keep
      sessionStorage.setItem('dr-robotin-messages', JSON.stringify(withoutFileData(messages)))
    } catch (e) {
      console.error('Error saving messages to storage:', e)
    }
  }, [messages])

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message])
  }, [])

  const clearConversation = useCallback(() => {
    setMessages([])
  }, [])

  const startNewConversation = useCallback(() => {
    // Save current conversation to sessions if it has messages
    if (messages.length > 0) {
      const currentSession: ConversationSession = {
        id: sessionId,
        messages: [...messages],
        createdAt: messages[0]?.timestamp || new Date().toISOString(),
        preview: messages.find(m => m.role === 'user')?.content.slice(0, 50) || 'Conversación'
      }
      setSessions(prev => {
        // Don't add duplicate sessions
        if (prev.some(s => s.id === currentSession.id)) {
          return prev.map(s => s.id === currentSession.id ? currentSession : s)
        }
        return [currentSession, ...prev]
      })
    }

    // Create new session
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('dr-robotin-session', newSessionId)
    sessionStorage.removeItem('dr-robotin-messages') // Clear persisted messages
    setSessionId(newSessionId)
    setMessages([])
  }, [messages, sessionId])

  const loadSession = useCallback((targetSessionId: string) => {
    const session = sessions.find(s => s.id === targetSessionId)
    if (session) {
      // Save current conversation first if it has messages
      if (messages.length > 0 && sessionId !== targetSessionId) {
        const currentSession: ConversationSession = {
          id: sessionId,
          messages: [...messages],
          createdAt: messages[0]?.timestamp || new Date().toISOString(),
          preview: messages.find(m => m.role === 'user')?.content.slice(0, 50) || 'Conversación'
        }
        setSessions(prev => {
          if (prev.some(s => s.id === currentSession.id)) {
            return prev.map(s => s.id === currentSession.id ? currentSession : s)
          }
          return [currentSession, ...prev]
        })
      }

      sessionStorage.setItem('dr-robotin-session', targetSessionId)
      setSessionId(targetSessionId)
      setMessages(session.messages)
      setHistoryOpen(false)
    }
  }, [sessions, messages, sessionId])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('/api/ia/doctor/sessions', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })
      const result = await response.json()
      if (result.success && result.data) {
        setSessions(result.data)
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const value: DoctorRobotinContextType = {
    messages,
    sessionId,
    sessions,
    loading,
    historyOpen,
    setMessages,
    addMessage,
    clearConversation,
    startNewConversation,
    loadSession,
    setHistoryOpen,
    fetchSessions
  }

  return (
    <DoctorRobotinContext.Provider value={value}>
      {children}
    </DoctorRobotinContext.Provider>
  )
}

export function useDoctorRobotin() {
  const context = useContext(DoctorRobotinContext)
  if (context === undefined) {
    throw new Error('useDoctorRobotin must be used within a DoctorRobotinProvider')
  }
  return context
}
